// Libs
import React, { useState } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { I18n } from '@aws-amplify/core';

// Assets
import add from '../assets/images/add.svg';
import remove from '../assets/images/remove.svg';

// Animations
const TransitionAnimation = (a, b) => keyframes`
  0% { transform: translateY(${a}) }
  100% { transform: translateY(${b}) }
`;

// Styles
const Container = styled.section`
  width: 100%;
  padding: 2rem 0 6rem;
`;

const Wrapper = styled.div`
  align-items: center;
  width: 90%;
  max-width: 1260px;
  margin: 0 auto;

  @media (max-width: 768px) {
    width: 100%;
  }
`;

const List = styled.ul`
  width: 75%;
  margin: 5rem auto 0;
  padding: 0;
  transition: all .4s;

  @media (max-width: 768px) {
    width: calc(100% - 80px);
  }

  @media (max-width: 480px) {
    width: 85%;
    margin-top: 3rem;
  }
`;

const BoxItem = styled.li`
  position: relative;
  margin: .75rem 0;
  overflow: hidden;
`;

const Item = styled.div`
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
  height: 3.9375rem;
  padding: 0 2rem 0 3.4375rem;
  border-radius: 8px;
  cursor: pointer;
  font: 300 1.0625rem 'Paralucent', sans-serif;
  background: #EBDAF8;
  transition: all .3s;
  z-index: 1;

  ${({ opened }) => opened && `
    font-weight: 500;
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  `}

  @media (min-width: 769px) {
    :hover {
      font-weight: 500;
    }
  }

  @media (max-width: 480px) {
    padding-left: .625rem;
    padding-right: 1.5625rem;
    font-size: .9375rem;
    font-weight: 300;
  }
`;

const Icon = styled.img`
  width: 1.125rem;

  @media (max-width: 480px) {
    width: .9375rem;
    margin-right: -.625rem;
  }
`;

const ItemDetail = styled.div`
  width: 100%;
  padding: .75rem 2rem;
  padding-left: 3.4375rem;
  background: #F8F0FF;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
  z-index: 0;

  ${({ closeQuestion }) => closeQuestion ? css`
    animation: ${TransitionAnimation('0', '-100%')} .4s;
  ` : css`
    animation: ${TransitionAnimation('-100%', '0')} .4s;
  `}

  @media (max-width: 480px) {
    padding: .25rem .625rem;
  }
`;

const Paragraph = styled.p`
  margin: 1rem 0;
  color: #22254B;
  font: 400 .875rem 'Soleto', sans-serif;
  line-height: 1.25rem;
  letter-spacing: .33px;

  @media (max-width: 480px) {
    font-size: .75rem;
    line-height: 1.1875rem;
  }
`;

const dict = {
  en: {
    'strytegy-free-question': 'Is Strytegy free to use?',
    'strytegy-free-answer': 'Yes, access to Strytegy and its features is completely free. Your use of the platform is really important for us, enjoy! :)',
    'strytegy-tools-question': 'Which tools will I find in Strytegy?',
    'strytegy-tools-answer': 'We provide an incredible set of tools for you collaborate with your team in the best way:',
    'strytegy-tools-organization-highligth': 'Organization - ',
    'strytegy-tools-organization': 'An organization allows you to create different workspaces, walls and canvas all within the same area, while also being provide access to other members of your teams.',
    'strytegy-tools-workspace-highligth': 'Workspaces - ',
    'strytegy-tools-workspace': 'Workspaces are folders in the organizations where the walls are accessed. They have up to 5 sublevels, whose access permissions can be managed, meaning you can decide who can have access where and when.',
    'strytegy-tools-agile-highligth': 'Agile Studio - ',
    'strytegy-tools-agile': 'In the Agile Room you are able to add Journeys and Templates to guide your processes of creation and collaboration in the most effective and interactive way.',
    'strytegy-tools-journey-highligth': 'Journeys - ',
    'strytegy-tools-journey': 'A journey is a set of canvases structured and curated in a certain order, in some cases, complemented by supporting materials, such as videos and texts, resulting in rich and powerful design, collaborative and learning eaxperiences.',
    'strytegy-tools-templates-highligth': 'Templates - ',
    'strytegy-tools-templates': 'A template is a visual way to organise information, post-its, pictures and other types of material, allowing people allowing documented definitions of unique business cases, project plans, design definitions and other business methodologies.',
    'strytegy-invite-question': 'Can I invited people to a project I have created?',
    'strytegy-invite-answer': 'Sure. Strytegy is an online collaboration environment, meaning that you can invite anyone you think can contribute to your project.',
    'strytegy-video-question': 'Is there a limit to the number of people on video calls?',
    'strytegy-video-answer': 'In Strytegy you can invite up to 30 members of your team to join you by video call, isn\'t this great?',
    'strytegy-journey-question': 'Can I create my own journeys?',
    'strytegy-journey-answer': 'While people often want to come together to work on a single canvas or template, more often than not they wish to work on complete end-to-end processes which involve various canvasses, templates and processes. Journeys are Strytegy\'s way to bring different components together in beautifully curated ways to help you and your teams align, collaborate and design in ever more meaningful ways.  You can organize your own daily work practices using Strytegy walls, or even contact our team to create a wall specially for you and your team.',
    'strytegy-limit-question': 'Is there a limit to the creation of folders and canvases/templates?',
    'strytegy-limit-answer': 'There are no limits for these. In addition to being free to create, the number of folders and templates that can be created on the platform is unlimited.',
    'strytegy-secure-workspace-question': 'What is a secure workspace?',
    'strytegy-secure-workspace-answer': 'Sometimes when we create a workspace we may only wish to allow some of our team members to access it. With the Security Workspace functionality, you can set the status of a workspace to "Public" (Visible to all members of the organization) or "Private" (restricted to only the members you invite).',
    'strytegy-details-question': 'Can I add extra details to a sticky note?',
    'strytegy-details-answer': 'Certainly! We understand that we are not always able to put all the information on a sticky note, so you can add  include additional information and attachments in the details section of the sticky notes.',
    'strytegy-payment-question': 'I work in a company and I would like to use Strytegy. Do I need to pay?',
    'strytegy-payment-answer': 'You don\'t have to. Strytegy\'s main features are free for both individuals and companies.',
    'strytegy-export-question': 'Can I export my documents in Strytegy to other formats?',
    'strytegy-export-answer': 'We know that we often need to share documents and information in specific formats. So in Strytegy you can export your Templates to pdf. and png. formats.',
  },
  'pt-BR': {
    'strytegy-free-question': 'O Strytegy é gratuito?',
    'strytegy-free-answer': 'Sim, o acesso ao Strytegy e suas funcionalidades é totalmente gratuito. A sua utilização é muito importante para a gente, aproveite! :)',
    'strytegy-tools-question': 'Que ferramentas encontrarei dentro do Strytegy?',
    'strytegy-tools-answer': 'Para que você possa colaborar com o seu time da melhor forma disponbilizamos um conjunto incrível de ferramentas:',
    'strytegy-tools-organization-highligth': 'Organização - ',
    'strytegy-tools-organization': 'Uma organização permite que você crie diferentes Workspaces, Walls e Canvases, todos dentro da mesma área, ao mesmo tempo que fornece acesso a outros membros de suas equipes.',
    'strytegy-tools-workspace-highligth': 'Workspaces - ',
    'strytegy-tools-workspace': 'Os espaços de trabalho são pastas nas organizações onde as Walls são acessadas. Eles têm até 5 subníveis, cujas permissões de acesso podem ser gerenciadas, o que significa que você pode decidir quem pode ter acesso, onde e quando.',
    'strytegy-tools-agile-highligth': 'Sala Ágil - ',
    'strytegy-tools-agile': 'Na sala ágil você consegue adicionar Jornadas e Templates para orientar o seu processo de criação e colaboração da forma mais efetiva e interativa possível.',
    'strytegy-tools-journey-highligth': 'Jornadas - ',
    'strytegy-tools-journey': 'Uma Jornada é um conjunto de telas em uma determinada ordem, com curadoria , e em alguns casos complementadas por materiais de apoio, como vídeos e textos, resultando em experiências de design, colaboração e aprendizagem ricas e potentes.',
    'strytegy-tools-templates-highligth': 'Templates - ',
    'strytegy-tools-templates': 'Um template é uma forma visual de organizar informações, notas, imagens e outros tipos de materiais, permitindo que as pessoas permitam definições documentadas de casos de negócios exclusivos, planos de projetos, definições de design e outras metodologias de negócios.',
    'strytegy-invite-question': 'Posso convidar pessoas para um projeto que eu criar?',
    'strytegy-invite-answer': 'Claro. O Strytegy é um ambiente online de colaboração, nele você pode convidar todos aqueles que entende que podem contribuir para o seu projeto.',
    'strytegy-video-question': 'Existe limite de pessoas nas vídeochamadas?',
    'strytegy-video-answer': 'No Strytegy você pode convidar até 30 membros do seu time  para se juntar a uma vídeochamada, isso não é ótimo?',
    'strytegy-journey-question': 'Posso criar minha própria Jornada?',
    'strytegy-journey-answer': 'Enquanto em algumas situações buscamos trabalhar em um único template, existem situações onde desejamos trabalhar em um processo de ponta a ponta envolvendo diversos templates e processos. As Jornadas são a forma do Strytegy trazer diferentes componentes juntos de forma organizada e hamônica para ajudar você e seu time se alinharem e colaborarem de forma mais efetiva. Você pode organizar suas práticas diárias utilizando Strytegy ou entrar em contato com o nosso time para criar uma jornada específica para você e seu time.',
    'strytegy-limit-question': 'Existe limite na criação de Pastas de Trabalho ou Canvas/Templates?',
    'strytegy-limit-answer': 'Não existe limite. Além de gratuito, o número de Pastas e Templates que podem ser criados na plataforma é ilimitado. ',
    'strytegy-secure-workspace-question': 'O que é uma secure workspace?',
    'strytegy-secure-workspace-answer': 'Muitas vezes quando criamos uma pasta de trabalho gostaríamos que só parte do time tenha acesso à ela. Com a funcionalidade da Security Workspace você pode definir o status de uma pasta de trabalho como "Público" (Vísivel para todos membros da organização) ou "Privado" (restrito para apenas os membros que você convidar).',
    'strytegy-details-question': 'Posso incluir detalhes em um post-it?',
    'strytegy-details-answer': 'Com certeza! Entendemos que nem sempre conseguimos colocar toda as informações em uma Nota, por isso criamos a possibilidade de incluir inforrmações complementares e anexos nos detalhes da Nota.',
    'strytegy-payment-question': 'Sou o gestor de uma empresa e gostaria que meu time utilizasse. Preciso pagar?',
    'strytegy-payment-answer': 'Não precisa. As funcionalidades do Strytegy são gratuitas tanto para pessoas físicas quanto empresas.',
    'strytegy-export-question': 'Consigo exportar meus documentos do Strytegy para outro formato?',
    'strytegy-export-answer': 'Sabemos que muitas vezes precisamos compartilhar documentos e informações em formatos específicos. Por isso, no Strytegy você pode exportar os seus Templates para os formatos pdf. png.',
  },
};

const faqList = [
  {
    id: 1,
    question: 'strytegy-free-question',
    answer: ['strytegy-free-answer']
  },
  // {
  //   id: 2,
  //   question: 'strytegy-tools-question',
  //   answer: [
  //     'strytegy-tools-answer',
  //     'strytegy-tools-organization',
  //     'strytegy-tools-workspace',
  //     'strytegy-tools-agile',
  //     'strytegy-tools-journey',
  //     'strytegy-tools-templates'
  //   ]
  // },
  {
    id: 3,
    question: 'strytegy-invite-question',
    answer: ['strytegy-invite-answer']
  },
  // {
  //   id: 4,
  //   question: 'strytegy-video-question',
  //   answer: ['strytegy-video-answer']
  // },
  // {
  //   id: 5,
  //   question: 'strytegy-journey-question',
  //   answer: ['strytegy-journey-answer']
  // },
  {
    id: 6,
    question: 'strytegy-limit-question',
    answer: ['strytegy-limit-answer']
  },
  // {
  //   id: 7,
  //   question: 'strytegy-secure-workspace-question',
  //   answer: ['strytegy-secure-workspace-answer']
  // },
  {
    id: 8,
    question: 'strytegy-details-question',
    answer: ['strytegy-details-answer']
  },
  {
    id: 9,
    question: 'strytegy-payment-question',
    answer: ['strytegy-payment-answer']
  },
  {
    id: 10,
    question: 'strytegy-export-question',
    answer: ['strytegy-export-answer']
  }
];

const FaqList = ({
  language
}) => {
  const [selectedQuestion, setQuestion] = useState(null);
  const [closeQuestion, setCloseQuestion] = useState(false);

  I18n.setLanguage(language === 'pt-BR' ? 'pt-BR' : 'en');
  I18n.putVocabularies(dict);

  const handleCloseQuestion = () => {
    setCloseQuestion(true);
    setTimeout(() => {
      setQuestion(null);
    }, 400);
  }

  const renderItems = () => {
    return faqList.map((item) => (
      <BoxItem
        key={item.id}
      >
        <Item
          onClick={() => {
            if (selectedQuestion === item.id) {
              handleCloseQuestion();
            } else {
              setCloseQuestion(false);
              setQuestion(item.id);
            }
          }}
          opened={selectedQuestion === item.id}
        >
          <span>{I18n.get(item.question)}</span>
          <Icon
            src={
              selectedQuestion === item.id
                ? remove
                : add
            }
            alt={
              selectedQuestion === item.id
                ? 'remove icon'
                : 'add icon'
            }
          />
        </Item>
        {selectedQuestion === item.id && (
          <ItemDetail
            closeQuestion={closeQuestion}
          >
            {item.answer.map((text, index) => (
              <Paragraph
                key={index}
              >
                {item.answer.length > 1 && index > 0 &&
                  <strong>
                    {I18n.get(`${text}-highligth`)}
                  </strong>
                }
                {I18n.get(text)}
              </Paragraph>
            ))}
          </ItemDetail>
        )}
      </BoxItem>
    ));
  }

  return (
    <Container id='faq-list'>
      <Wrapper>
        <List>
          {renderItems()}
        </List>
      </Wrapper>
    </Container>
  );
};

export default FaqList;
